import { useMemo } from 'react'

import { getRouteApi } from '@tanstack/react-router'

import { resolveTimeRange } from '@/shared/opensearch/utils/time-preset'

import type {
	PageContext,
	SearchRuleGroup,
	SearchTimeRangeContext,
} from '@/features/ai/types'

const Route = getRouteApi('/_dashboard/search/')

/**
 * PageContext provider for the `/search` route.
 *
 * Maps the route's URL search params to Noah's `SearchOverviewContext`
 * (backend: `bitlyft_noah/models/page_context.py::SearchOverviewContext` +
 * `bitlyft_noah/query/zod_converter.py::ZodSearchQuery`).
 *
 * `q` is now a native RuleGroup object in the URL (no longer JSON-encoded);
 * pass through unchanged. `tr` may be a rolling preset — resolve to absolute
 * ISO bounds before sending to the BE.
 */
export const useSearchPageContext = (): PageContext => {
	const { int, q, tr } = Route.useSearch()

	// Memoize so consumers get a stable reference — `AIChatInner` writes this
	// to a vanilla store via `useEffect`, which would otherwise fire on every
	// render since the object literal is fresh each call.
	return useMemo<PageContext>(() => {
		const ruleGroup = q as SearchRuleGroup | undefined
		const resolved = resolveTimeRange(tr)
		const absoluteTr: SearchTimeRangeContext | undefined = resolved
			? { st: resolved.st, ed: resolved.ed }
			: undefined

		return {
			page_type: 'search_overview',
			...(int !== undefined && { int }),
			...(ruleGroup !== undefined && { q: ruleGroup }),
			...(absoluteTr !== undefined && { tr: absoluteTr }),
		}
	}, [int, q, tr])
}
